import { postDocHasContent, youtubeId } from "@baze/schema";
import type { ReactNode } from "react";
import type { RenderContext, SectionProps } from "../context";
import { domId, field } from "../context";
import { PostBody } from "../blog";
import { Icon } from "../icons";
import { BUTTON, CARD_HOVER, Container, Img, MultiLine, STACK, STAGGER, SectionHeader, SectionShell, SiteLink, cx } from "../ui";

type Content = SectionProps<"lessons">["node"]["content"];
type Group = Content["groups"][number];
type Topic = Group["topics"][number];

export interface LessonEntry {
  topic: Topic;
  group: Group;
  gi: number;
  ti: number;
  /** Global position, 1-based. */
  n: number;
  /** DOM id of the topic's detail view (also its shareable #link). */
  id: string;
}

/** Group card tints, cycling through the design's own colours. */
const TINTS = ["bg-site-primary/10", "bg-site-secondary/15", "bg-site-accent/15", "bg-site-text/5"];

const slug = (s: string) =>
  s
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

/**
 * Topics in reading order, each with a stable id derived from the anchor and its title (≤64 chars,
 * the in-page href limit). Duplicate titles get -2, -3…
 */
export function lessonEntries(anchor: string, c: Content): LessonEntry[] {
  const seen = new Map<string, number>();
  const out: LessonEntry[] = [];
  c.groups.forEach((group, gi) =>
    group.topics.forEach((topic, ti) => {
      const base = `${anchor}-${slug(topic.title) || "materi"}`.slice(0, 58).replace(/-+$/, "");
      const k = (seen.get(base) ?? 0) + 1;
      seen.set(base, k);
      out.push({ topic, group, gi, ti, n: out.length + 1, id: k > 1 ? `${base}-${k}` : base });
    }),
  );
  return out;
}

const pad = (n: number) => String(n).padStart(2, "0");

/** Topics per page when the owner has not chosen (0 = no paging). */
export const LESSONS_PER_PAGE = 8;

/** In-page link. Inert in the editor, like every other engine link. */
function Hash({ ctx, to, className, children, group }: { ctx: RenderContext; to: string; className?: string; children: ReactNode; group?: number }) {
  return (
    <a href={`#${to}`} className={className} {...(group !== undefined ? { "data-baze-lesson-group": String(group) } : {})} {...(ctx.editor ? { "data-baze-inert": "" } : {})}>
      {children}
    </a>
  );
}

/** Prev / next / close inside the topic dialog: plain #links, also driven by ←, → and Esc (runtime). */
function Go({ ctx, to, dir, className, label, children }: { ctx: RenderContext; to: string; dir: "prev" | "next" | "close"; className?: string; label?: string; children: ReactNode }) {
  return (
    <a href={`#${to}`} data-baze-lesson-to={dir} aria-label={label} className={className} {...(ctx.editor ? { "data-baze-inert": "" } : {})}>
      {children}
    </a>
  );
}

/**
 * One topic as a dialog over the list: a bottom sheet on phones, a large centred panel on wider
 * screens. Its #link opens it (CSS :target, no JS needed); the backdrop and × go back to the list.
 * Sticky header (group, "Materi 3 dari 20", reading progress, close) and footer (previous / next).
 */
function Detail({ ctx, c, e, prev, next, total, listId }: { ctx: RenderContext; c: Content; e: LessonEntry; prev?: LessonEntry; next?: LessonEntry; total: number; listId: string }) {
  const t = e.topic;
  const p = `content.groups.${e.gi}.topics.${e.ti}`;
  const vid = youtubeId(t.video);
  const titleId = domId(ctx, `${e.id}-judul`);
  const close = c.backLabel || "Tutup materi";
  return (
    <div id={domId(ctx, e.id)} data-baze-lesson-detail="" role="dialog" aria-modal="true" aria-labelledby={titleId} className="baze-lesson-modal">
      <a href={`#${listId}`} aria-hidden="true" tabIndex={-1} className="baze-lesson-backdrop" {...(ctx.editor ? { "data-baze-inert": "" } : {})} />
      <article data-baze-lesson-panel="" tabIndex={-1} className="baze-lesson-panel">
        <header className="baze-lesson-bar">
          <span className="min-w-0 truncate rounded-site-sm bg-site-primary/10 px-2.5 py-1 text-xs font-semibold tracking-wide text-site-primary uppercase">{e.group.label || e.group.title}</span>
          <span className="shrink-0 text-xs font-medium text-site-muted tabular-nums">{`Materi ${e.n} dari ${total}`}</span>
          <Go ctx={ctx} to={listId} dir="close" label={close} className="ml-auto grid size-10 shrink-0 place-items-center rounded-full text-site-text transition hover:bg-site-text/10">
            <Icon name="close" className="size-5" />
          </Go>
          <span aria-hidden="true" className="baze-lesson-progress" />
        </header>

        <div className="px-5 pt-6 pb-10 @3xl:px-12 @3xl:pt-10">
          <h3 id={titleId} className="baze-heading text-[1.75rem] leading-tight @3xl:text-[2.5rem]" {...field(ctx, `${p}.title`)}>
            {t.title}
          </h3>
          {e.group.label ? <p className="mt-2 font-semibold text-site-muted">{e.group.title}</p> : null}
          {t.summary ? (
            <div className="mt-6 rounded-site-md border-l-4 border-site-primary bg-site-primary/8 p-5 @3xl:p-6">
              <p className="text-xs font-bold tracking-[0.12em] text-site-primary uppercase">Ringkasan materi</p>
              <MultiLine ctx={ctx} text={t.summary} path={`${p}.summary`} className="mt-2 leading-relaxed text-site-text @3xl:text-lg" />
            </div>
          ) : null}
          {t.image ? (
            <div className="mt-7 aspect-[16/9] overflow-hidden rounded-site-md bg-site-surface">
              <Img ctx={ctx} image={t.image} sizes="(min-width: 960px) 860px, 100vw" path={`${p}.image`} />
            </div>
          ) : null}
          {t.points?.length ? (
            <ul className="mt-7 grid gap-2.5 @2xl:grid-cols-2">
              {t.points.map((pt, i) => (
                <li key={i} className="flex gap-3 rounded-site-md bg-site-surface p-3.5 text-site-text">
                  <span className="grid size-6 shrink-0 place-items-center rounded-full bg-site-primary text-site-on-primary">
                    <Icon name="check" className="size-3.5" />
                  </span>
                  <span className="leading-snug" {...field(ctx, `${p}.points.${i}`)}>
                    {pt}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
          {vid ? (
            <div className="mt-8 aspect-video overflow-hidden rounded-site-md bg-site-text/10 shadow-site-sm">
              {ctx.thumbnail ? null : (
                <iframe
                  src={`https://www.youtube-nocookie.com/embed/${vid}`}
                  title={`Video: ${t.title}`}
                  loading="lazy"
                  allow="accelerometer; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  referrerPolicy="strict-origin-when-cross-origin"
                  className="h-full w-full border-0"
                />
              )}
            </div>
          ) : null}
          {postDocHasContent(t.doc) ? (
            <div className="baze-lesson-prose mt-6">
              <PostBody doc={t.doc!} />
            </div>
          ) : (
            <MultiLine ctx={ctx} text={t.body} path={`${p}.body`} className="mt-8 text-[1.0625rem] leading-[1.8] text-site-text/90 @3xl:text-lg" />
          )}
          {t.pdf || (vid && t.video) ? (
            <div className="mt-8 flex flex-wrap gap-3">
              {t.pdf ? (
                <SiteLink ctx={ctx} href={t.pdf} trackLink fallback="none" className={cx("baze-btn", BUTTON.primary)}>
                  <Icon name="download" className="size-[1.125rem] shrink-0" />
                  <span>{c.pdfLabel || "Buka PDF"}</span>
                </SiteLink>
              ) : null}
              {vid && t.video ? (
                <SiteLink ctx={ctx} href={t.video} trackLink fallback="none" className={cx("baze-btn", BUTTON.secondary)}>
                  <Icon name="youtube" className="size-[1.125rem] shrink-0" />
                  <span>{c.videoLabel || "Tonton di YouTube"}</span>
                </SiteLink>
              ) : null}
            </div>
          ) : null}
          {c.note ? (
            <aside className="mt-10 flex gap-3 rounded-site-md bg-site-accent/15 p-5">
              <Icon name="heart" className="mt-0.5 size-5 shrink-0 text-site-accent" />
              <div>
                {c.note.title ? (
                  <p className="font-semibold text-site-text" {...field(ctx, "content.note.title")}>
                    {c.note.title}
                  </p>
                ) : null}
                <MultiLine ctx={ctx} text={c.note.body} path="content.note.body" className="mt-1 text-sm leading-relaxed text-site-text/85" />
              </div>
            </aside>
          ) : null}
        </div>

        <footer className="baze-lesson-foot">
          {prev ? (
            <Go ctx={ctx} to={domId(ctx, prev.id)} dir="prev" className="baze-lesson-step-link">
              <Icon name="chevron-left" className="size-4 shrink-0" />
              <span className="min-w-0">
                <span className="block text-[11px] font-semibold tracking-wide text-site-muted uppercase">Sebelumnya</span>
                <span className="block truncate">{prev.topic.title}</span>
              </span>
            </Go>
          ) : (
            <span />
          )}
          {next ? (
            <Go ctx={ctx} to={domId(ctx, next.id)} dir="next" className="baze-lesson-step-link baze-lesson-step-next">
              <span className="min-w-0 text-right">
                <span className="block text-[11px] font-semibold tracking-wide uppercase opacity-80">Berikutnya</span>
                <span className="block truncate">{next.topic.title}</span>
              </span>
              <Icon name="chevron-right" className="size-4 shrink-0" />
            </Go>
          ) : (
            <Go ctx={ctx} to={listId} dir="next" className="baze-lesson-step-link baze-lesson-step-next justify-center">
              <Icon name="check" className="size-4 shrink-0" />
              <span>Selesai</span>
            </Go>
          )}
        </footer>
      </article>
    </div>
  );
}

/**
 * Learning material. The list and every topic's detail view are in the HTML; CSS `:target` shows one
 * detail in place of the list, so each topic has its own shareable #link and works without JS. The
 * lessons runtime script only adds search and group filtering on top.
 */
export function Lessons({ node, ctx }: SectionProps<"lessons">) {
  const c = node.content;
  const cards = node.variant === "cards";
  const entries = lessonEntries(node.anchor, c);
  const listId = domId(ctx, `${node.anchor}-daftar`);
  const showSearch = c.showSearch !== false;
  const showFilter = c.showFilter !== false && c.groups.length > 1;
  const perPage = c.perPage ?? LESSONS_PER_PAGE;
  const paged = perPage > 0 && entries.length > perPage;
  // Live pages ship every row; rows past the first page are hidden by CSS only where scripts run (the
  // runtime then pages them), so a visitor without JS still gets the whole list.
  const later = (i: number) => paged && !ctx.editor && i >= perPage;
  return (
    <SectionShell ctx={ctx} node={node} className="baze-lessons">
      <Container>
        <div data-baze-lesson-list="" data-per={paged ? String(perPage) : undefined}>
          <SectionHeader ctx={ctx} node={node} eyebrow={c.eyebrow} title={c.title} description={c.description} />
          {cards && c.groups.length > 1 ? (
            <ul className={cx(STACK, STAGGER, "grid grid-cols-2 gap-3 @4xl:grid-cols-4 @3xl:gap-5")}>
              {c.groups.map((g, gi) => (
                <li key={gi}>
                  <Hash ctx={ctx} to={listId} group={gi} className={cx("flex h-full flex-col rounded-site-md p-4 @3xl:p-5", TINTS[gi % TINTS.length], CARD_HOVER)}>
                    {g.label ? (
                      <span className="text-xs font-semibold text-site-muted" {...field(ctx, `content.groups.${gi}.label`)}>
                        {g.label}
                      </span>
                    ) : null}
                    <span className="baze-heading mt-1 text-base leading-snug @3xl:text-lg" {...field(ctx, `content.groups.${gi}.title`)}>
                      {g.title}
                    </span>
                    <span className="mt-auto pt-3 text-xs text-site-muted">{`${g.topics.length} topik`}</span>
                  </Hash>
                </li>
              ))}
            </ul>
          ) : null}
          <div id={listId} className={cx(STACK, "scroll-mt-28")}>
            {showSearch || showFilter ? (
              // Revealed by the runtime script: without JS the controls would do nothing.
              <div data-baze-lesson-ui="" hidden className="mb-5 space-y-3">
                {showSearch ? (
                  <label className="flex min-h-12 items-center gap-2 rounded-site-md border border-site-border bg-site-surface px-4 focus-within:border-site-primary">
                    <span className="sr-only">Cari materi</span>
                    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="size-4 shrink-0 text-site-muted">
                      <circle cx="11" cy="11" r="7" />
                      <path d="m20 20-3.5-3.5" />
                    </svg>
                    <input type="search" data-baze-lesson-search="" placeholder={c.searchPlaceholder || "Cari materi…"} className="min-w-0 flex-1 bg-transparent py-2 text-site-text outline-none placeholder:text-site-muted" />
                  </label>
                ) : null}
                {showFilter ? (
                  <div role="group" aria-label="Saring materi" className="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none]">
                    <button type="button" data-baze-lesson-filter="" aria-pressed="true" className="baze-lesson-chip">
                      {`Semua (${entries.length})`}
                    </button>
                    {c.groups.map((g, gi) => (
                      <button key={gi} type="button" data-baze-lesson-filter={String(gi)} aria-pressed="false" className="baze-lesson-chip">
                        {g.label || g.title}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}
            <ol className={cx(STAGGER, "space-y-3")}>
              {entries.map((e, i) => {
                const p = `content.groups.${e.gi}.topics.${e.ti}`;
                return (
                  <li key={e.id} data-baze-lesson-row="" {...(later(i) ? { "data-baze-lesson-later": "" } : {})} data-group={String(e.gi)} data-text={`${e.topic.title} ${e.topic.summary ?? ""} ${e.group.title}`.toLowerCase()}>
                    <Hash ctx={ctx} to={domId(ctx, e.id)} className={cx("group flex items-center gap-4 rounded-site-md border border-site-border bg-site-surface p-4 shadow-site-sm", CARD_HOVER)}>
                      <span aria-hidden="true" className="grid size-11 shrink-0 place-items-center rounded-full bg-site-primary/10 text-sm font-bold text-site-primary tabular-nums">
                        {pad(e.n)}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block font-semibold leading-snug text-site-text @3xl:text-lg" {...field(ctx, `${p}.title`)}>
                          {e.topic.title}
                        </span>
                        {e.topic.summary ? <span className="mt-0.5 line-clamp-2 block text-sm text-site-muted">{e.topic.summary}</span> : null}
                        {c.groups.length > 1 ? <span className="mt-2 inline-block rounded-site-sm bg-site-text/5 px-2 py-0.5 text-xs font-semibold text-site-muted">{e.group.label || e.group.title}</span> : null}
                      </span>
                      <Icon name="chevron-right" className="size-5 shrink-0 text-site-muted transition-transform group-hover:translate-x-0.5" />
                    </Hash>
                  </li>
                );
              })}
            </ol>
            <p data-baze-lesson-empty="" hidden className="py-8 text-center text-site-muted">
              Materi tidak ditemukan.
            </p>
            {paged && ctx.editor ? (
              <p className="mt-4 rounded-site-md border border-dashed border-site-border px-4 py-3 text-center text-sm text-site-muted">
                {`Pengunjung melihat ${perPage} materi per halaman (${Math.ceil(entries.length / perPage)} halaman). Semua materi tampil di editor.`}
              </p>
            ) : null}
            {paged && !ctx.editor ? (
              // "Halaman 2 dari 6" and the range are filled in by the runtime script (they follow search and filter).
              <nav data-baze-lesson-pager="" hidden aria-label="Halaman daftar materi" className="mt-6 flex flex-col items-center gap-3">
                <div className="flex items-center justify-center gap-2">
                  <button type="button" data-baze-lesson-prev="" className="baze-lesson-page baze-lesson-step" aria-label="Halaman sebelumnya">
                    <Icon name="chevron-left" className="size-4" />
                    <span className="hidden @md:inline">Sebelumnya</span>
                  </button>
                  <span data-baze-lesson-pages="" aria-live="polite" className="min-w-28 px-2 text-center text-sm font-semibold text-site-text tabular-nums" />
                  <button type="button" data-baze-lesson-next="" className="baze-lesson-page baze-lesson-step" aria-label="Halaman berikutnya">
                    <span className="hidden @md:inline">Berikutnya</span>
                    <Icon name="chevron-right" className="size-4" />
                  </button>
                </div>
                <p data-baze-lesson-info="" className="text-xs text-site-muted" />
              </nav>
            ) : null}
          </div>
        </div>
        {entries.map((e, i) => (
          <Detail key={e.id} ctx={ctx} c={c} e={e} prev={entries[i - 1]} next={entries[i + 1]} total={entries.length} listId={listId} />
        ))}
      </Container>
    </SectionShell>
  );
}
